/* -------------------------------------------------------------------------- */
/*                           GeofenceMap Component                           */
/* -------------------------------------------------------------------------- */
// Componente de mapa para editar geofences usando React-Leaflet
// Reemplaza el uso de MapsBasic para casos específicos de edición de geocercas

import { LatLngExpression, Map } from 'leaflet';
import { useEffect, useState, useRef } from 'react';
import { useIntl } from 'react-intl';
import { MapContainer, useMapEvents } from 'react-leaflet';
import { useSelector } from 'react-redux';
import * as authSelectors from '../../../../../redux/reducers/auth';
import MapBaseLayers from '../MapBaseLayers';
import MapGeofenceEdit from '../MapGeofenceEdit';
import MapRouteGeofenceEdit from '../MapRouteGeofenceEdit';
import { MapProvider } from '../../context/GoogleMapsContext';

export interface GeofenceMapProps {
  /** Configuración básica del mapa */
  mapOptions?: {
    className?: string;
    style?: React.CSSProperties;
    center?: LatLngExpression;
    zoom?: number;
    rerenderMap?: Date | React.Key;
  };
  /** Configuración del geofence a editar */
  geofenceEditOptions?: {
    geofence?: {
      geofenceId?: string;
      geofenceName?: string;
      geofenceWktString?: string;
      shape?: 'C' | 'R' | 'P' | 'U'; // Circle, Rectangle, Polygon, User-defined
      radius?: number;
      color?: string;
      latitude?: number;
      longitude?: number;
      [key: string]: any;
    };
    onGeofenceEdit?: (values: any) => void;
    onGeofenceDragend?: (values: any) => void;
    showGeofence?: boolean;
    usePopovers?: boolean;
    isMapFullScreen?: boolean;
  };
  /** Clave única para forzar re-render del mapa */
  mapKey?: string;
  /** Instancia del mapa para control externo */
  mapInstance?: number;
}

const GeofenceMap = (props: GeofenceMapProps) => {
  const intl = useIntl();
  const { store } = useSelector((state) => ({
    store: state
  }));

  const {
    mapOptions = {},
    geofenceEditOptions = {},
    mapKey,
    mapInstance
  } = props;

  const {
    className = 'map-full-height-no-footer',
    style = { height: '40vh' },
    center,
    zoom,
    rerenderMap
  } = mapOptions;

  // Valores por defecto del centro y zoom basados en las preferencias del usuario
  const getInitialCenter = (): LatLngExpression => {
    // Prioridad: 1) center prop, 2) geofence ubicación, 3) centro de usuario, 4) default
    if (center) return center;
    if (geofenceEditOptions.geofence?.latitude && geofenceEditOptions.geofence?.longitude) {
      return [geofenceEditOptions.geofence.latitude, geofenceEditOptions.geofence.longitude];
    }
    return (authSelectors.getAuthUserMapHomeCenter(store) as LatLngExpression) || [51.5074, 0.1278];
  };
  
  const initialMapCenter: LatLngExpression = getInitialCenter();
  const initialZoom = zoom || authSelectors.getAuthUserMapHomeZoom(store) || 15;

  // Estado del mapa
  const [map, setMap] = useState<Map | undefined>();
  const [mapCenter, setMapCenter] = useState<LatLngExpression>(initialMapCenter);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [listenMapControlChangesLayers, setMapControlChangesLayers] = useState<
    undefined | (string | boolean)[]
  >(undefined);

  // Componente para manejar eventos del mapa
  const MapEvents = () => {
    const mapInstance = useMapEvents({
      load: () => {
        setMap(mapInstance);
        setIsInitialized(true);
      }
    });
    return null;
  };

  // Configurar cambios de control del mapa para las capas base
  useEffect(() => {
    // @ts-ignore MapQuest
    setMapControlChangesLayers([typeof MQ, intl.locale]);
  }, [intl.locale]);

  // Invalidar tamaño del mapa cuando cambie rerenderMap (sin resetear posición)
  useEffect(() => {
    if (map != null && rerenderMap) {
      setTimeout(function () {
        // Guardar la posición actual antes de invalidar el tamaño
        const currentCenter = map.getCenter();
        const currentZoom = map.getZoom();
        map.invalidateSize(false); // false para no resetear la vista
        // Restaurar la posición si es necesario
        if (currentCenter && currentZoom) {
          map.setView(currentCenter, currentZoom, { animate: false });
        }
      }, 100);
    }
  }, [map, rerenderMap]);

  // Configurar idioma del mapa
  useEffect(() => {
    // @ts-ignore locale does return lang
    if (map) map.pm.setLang(intl.locale);
  }, [map, intl]);

  // Configuración inicial del mapa sin auto-centrado
  useEffect(() => {
    if (map && isInitialized) {
      // Mapa inicializado, sin auto-centrado para permitir navegación libre
    }
  }, [map, isInitialized]);

  // Actualizar centro del mapa solo cuando se proporciona explícitamente
  useEffect(() => {
    // Solo actualizar el centro si viene desde props y el mapa no está inicializado
    if (!isInitialized && center) {
      setMapCenter(center);
    }
  }, [center, isInitialized]);

  return (
    <MapProvider>
      <MapContainer
        key={`geofence-map`}
        className={className}
        center={mapCenter}
        preferCanvas={true}
        zoom={initialZoom}
        style={style}
      >
        <MapBaseLayers 
          listenMapControlChanges={listenMapControlChangesLayers}
          position="bottomleft"
        />
        <MapEvents />
        
        {/* Componente de edición de geofence si está configurado */}
        {geofenceEditOptions.showGeofence && (
          <>
            {geofenceEditOptions.geofence?.isRouteGeofence ? (
              <MapRouteGeofenceEdit 
                {...geofenceEditOptions}
              />
            ) : (
              <MapGeofenceEdit 
                {...geofenceEditOptions}
              />
            )}
          </>
        )}
      </MapContainer>
    </MapProvider>
  );
};

// Props por defecto
GeofenceMap.defaultProps = {
  mapOptions: {
    className: 'map-full-height-no-footer',
    style: { height: '40vh' },
    zoom: 13
  },
  geofenceEditOptions: {
    showGeofence: false,
    usePopovers: false,
    isMapFullScreen: false
  }
};

export default GeofenceMap;